import ControlPointIcon from "@mui/icons-material/ControlPoint";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Collapse, Grid, IconButton, styled } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as constants from "../../../constants";
import CustomTextField from "../../../shared/CustomTextFeild";
import { RootState } from "../../../store/store";
import { addMasterChartAccounts, deleteMasterChartAccounts } from "../services/Financials/budget.service";
import { MasterChartAccountsVO } from "../services/Financials/financials";
import CommonUtilities from "../Utils/CommonUtilities";
import { formatCurrency } from "../Utils/formatUtils";
import { notifyUser } from "../Utils/notifyUser";
import ReusableSubChartsAccountTable from "./ReusableSubChartsAccountTable";


export interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any) => string;
  typeEdit?: boolean;
}

interface ReusableTableProps {
  tableKey?: string;
  columns: Column[];
  rows: Record<string, any>[];
  chartAccountsVOList?: any[];
  loading?: boolean;
  readOnlyForecast?: boolean;
  containerClass?: string;
  forecastYear?: string;
  defaultParticipantId?: number;
  totalAmount?: number;
  isExpand?: boolean;
  pageType?: string;
  type?: string;
  typeId?: number;
  onDataChange?: (updatedData: Record<string, any>[]) => void;
  setToRefresh?: (value: boolean) => void;
  refreshKey?: number;
}

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  "& .MuiTableCell-root": {
    border: "0.5px solid #f2f6fa",
  },
  borderBottom: "1px solid #dadcdc"
}));
interface GLMapping {

  forecastSubChartId: number,
  displayName: string;
  type: string;
  subChartAccountNumber: number
}
const ReusableChartsAccountTable: React.FC<ReusableTableProps> = ({
  tableKey,
  columns,
  rows,
  chartAccountsVOList,
  loading = false,
  readOnlyForecast = false,
  forecastYear,
  totalAmount,
  isExpand,
  pageType,
  type,
  typeId,
  onDataChange,
  setToRefresh,
  refreshKey = 0,
}) => {
  const [loadingFlag, setLoadingFlag] = useState(false);
  // store sub-accounts per parent row index to avoid huge flattened arrays
  const [subChartAccountsList, setSubChartAccountsList] = useState<Record<number, any[]>>({});
  const [stateRows, setStateRows] = useState(rows);
  const [currentYear, setCurrentYear] = useState(0);
  const [editFlags, setEditFlags] = useState<{ [key: number]: boolean }>({});
  const userDetails = useSelector((state: RootState) => state.auth);
  const [accountNumber, setAccountNumber] = useState("");
  const [description, setDescription] = useState("");
  const [updatedSubchartRows, setUpdatedSubchartRows] = useState<any[]>([]);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);


  useEffect(() => {
    // Toggle expand-all. If expanding, build an index -> subAccounts map (keeps sub-accounts grouped by row).
    if (isExpand) {
      const allIndices = stateRows.map((_, index) => index);
      setExpandedRows(allIndices);
      // build per-index map (avoids a single giant flat array)
      const perIndexMap: Record<number, any[]> = {};
      stateRows.forEach((r, i) => {
        perIndexMap[i] = pageType === "BudgetAccountDetails"
          ? (r.masterSubChartAccountsList ?? [])
          : (r.forecastSubChartAccountsList ?? []);
      });
      setSubChartAccountsList(perIndexMap);
    } else {
      setExpandedRows([]);
    }
  }, [isExpand, pageType]);

  useEffect(() => {
    if (rows) {
      setStateRows(rows ? rows : []);
    }
  }, [rows]);
  useEffect(() => {
    if (forecastYear && forecastYear.length === 4) {
      const currentYear = parseInt(forecastYear.substring(2, 4)) + 2000;
      setCurrentYear(currentYear);
    }
  }, [forecastYear]);

  useEffect(() => {
    if (onDataChange) {
      onDataChange(updatedSubchartRows);
    }
  }, [updatedSubchartRows, onDataChange]);

  useEffect(() => {
    setExpandedRows([]);
  }, [refreshKey]);

  const TableColumns: Column[] = [
    { id: "accountNumber", label: pageType === "BudgetAccountDetails" ? translate('subChartAccounts') : translate('subChartAccountNo'), minWidth: 50, align: "center", typeEdit: false },
    { id: "accountName", label: translate('description'), minWidth: 150, align: "left", typeEdit: false },
    { id: "prevBudget", label: `${translate('budget')} (${currentYear - 2} - ${currentYear - 1})`, minWidth: 150, align: "right", typeEdit: false },
    { id: "curBudget", label: `${translate('budget')} (${currentYear - 1} - ${currentYear})`, minWidth: 100, align: "right", typeEdit: true },
    { id: "fullBudget", label: translate('actualYTD'), minWidth: 150, align: "right", typeEdit: false },
    { id: "lastYearYTD", label: translate('lastYearYTD'), minWidth: 100, align: "right", typeEdit: false },
    { id: "budgetAmount", label: translate('budget'), minWidth: 150, align: "right", typeEdit: false },
    { id: "actualAmount", label: translate('actual'), minWidth: 100, align: "right", typeEdit: false },
    { id: "varianceAmount", label: translate('varianceAmount'), minWidth: 150, align: "right", typeEdit: false },
    { id: "variancePercentage", label: translate('variancePercentage'), minWidth: 100, align: "right", typeEdit: false },
    { id: "fullBudgets", label: translate('actualYTD'), minWidth: 100, align: "right", typeEdit: false },
    { id: "fullBudgetAmount", label: translate('fullYearBudget'), minWidth: 100, align: "right", typeEdit: false },
    { id: "fullYearBudgetRemaining", label: translate('fullYearBudgetRemaining'), minWidth: 150, align: "right", typeEdit: false },
    { id: "percentageBudgetRemaining", label: translate('budgetRemaining'), minWidth: 100, align: "right", typeEdit: false },
    { id: "editDelete", label: translate('editremove'), minWidth: 150, align: "center" },
  ];
  const filterColumns = (tableKey: string) => {
    if (["chartBudgetIncomeAccountsListing", "chartBudgetExpensesAccountsListing"].includes(tableKey)) {
      return TableColumns.filter(column =>
        ["accountNumber", "accountName", "prevBudget", "curBudget"].includes(column.id)
      );
    } else if (["chartAssetAccountsListing", "chartLiabilitiesAccountsListing", "chartNetAssetsAccountsListing"].includes(tableKey)) {
      return TableColumns.filter(column =>
        ["accountNumber", "accountName", "fullBudget", "lastYearYTD"].includes(column.id)
      );
    } else if (["chartActualsIncomeAccountsListing", "chartActualsExpensesAccountsListing"].includes(tableKey)) {
      return TableColumns.filter(column =>
        !["prevBudget", "curBudget", "fullBudget", "lastYearYTD", "editDelete"].includes(column.id)
      );
    } else if (tableKey === "BudgetAccountDetails") {
      return TableColumns.filter(column =>
        ["accountNumber", "accountName", "editDelete"].includes(column.id)
      );
    } else {
      return TableColumns;
    }
  };

  const handleExpandClick = (index: number, row: any) => {
    const subAccounts = pageType === "BudgetAccountDetails" ? (row.masterSubChartAccountsList ?? []) : (row.forecastSubChartAccountsList ?? []);
    setSubChartAccountsList(prev => ({ ...prev, [index]: subAccounts }));
    setExpandedRows(prev => (prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]));
  };

  const handleSaveClick = (index: any, row: MasterChartAccountsVO) => {
    const updatedRow = {
      ...row,
    };
    toAddMasterChartAccounts(updatedRow);
    setEditFlags((prev) => ({
      ...prev,
      [index]: false,
    }));
  };
  const handleEditClick = (index: any, row: any) => {

    setEditingRowIndex(index);
    setEditFlags((prev) => ({
      ...prev,
      [index]: true,
    }));
  };
  async function toAddMasterChartAccounts(masterChartAccountsVO: any) {
    setLoadingFlag(true)
    try {
      const responseList = await addMasterChartAccounts(masterChartAccountsVO);
      if (responseList?.status === constants.STATUS_CODES.SUCCESS) {

        notifyUser(responseList?.data?.message, 'success', translate('budgetAccountDetails'));
      } else {
        notifyUser(responseList?.data?.message, 'error', translate('budgetAccountDetails'));
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(translate('axiosError'), error.response?.data ?? error.message);
      } else {
        console.error(translate('unexpectedError'), error);
      }
    } finally {
      setToRefresh?.(true);
      setLoadingFlag(false)
    }
  }
  async function handleDeleteClick(index: number, row: any) {

    try {
      const responseList = await deleteMasterChartAccounts(row.id, userDetails.clientId);
      if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
        notifyUser(responseList?.data?.message, 'success', translate('budgetAccountDetails'));
        setToRefresh?.(true);
      } else {
        notifyUser(responseList?.data?.message, 'error', translate('budgetAccountDetails'));
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(translate('axiosError'), error.response?.data ?? error.message);
      } else {
        console.error(translate('unexpectedError'), error);
      }
    } finally {
      setLoadingFlag(false)
    }
  }

  const transformSubAccounts = (subAccounts: any[] = []) =>
    subAccounts.map((item: any) => ({
      ...item,
      accountNumber: item.accountNumber ?? "",
      prevBudget: item.prevBudget ? formatCurrency(item.prevBudget) : "$0.00",
      curBudget: item.curBudget ? formatCurrency(item.curBudget) : "$0.00",
      fullBudget: item.fullActualAmount ? formatCurrency(item.fullActualAmount) : "$0.00",
      fullBudgets: "-",
      lastYearYTD: item.fullBudget ? formatCurrency(item.fullBudget) : "$0.00",
      actualAmount: item.fullActualAmount ? formatCurrency(item.fullActualAmount) : "$0.00",
      budgetAmount: item.fullBudget ? formatCurrency(item.fullBudget) : "$0.00",
      varianceAmount: item.fullActualAmount && item.fullBudget ? formatCurrency(item.fullActualAmount - item.fullBudget) : "$0.00",
      fullBudgetAmount: item.fullBudget ? formatCurrency(item.fullBudget) : "$0.00",
      fullYearBudgetRemaining: item.fullActualAmount && item.fullBudget ? formatCurrency(item.fullActualAmount - item.fullBudget) : "$0.00",
      variancePercentage: item.fullActualAmount && item.fullBudget ? `${(((item.fullActualAmount - item.fullBudget) / item.fullBudget) * 100).toFixed(2)}%` : "-",
      percentageBudgetRemaining: item.fullActualAmount && item.fullBudget ? `${(((item.fullActualAmount - item.fullBudget) / item.fullBudget) * 100).toFixed(2)}%` : "-",
    }));

  const handleForecastChange = (month: string, newValue: string, rowIndex: number) => {
    const value = newValue;
    if (value.includes('-')) return;
    const [integerPart, fractionalPart] = value.split('.');
    if (integerPart.length > 12) return;
    if (fractionalPart && fractionalPart.length > 2) return;

    const updatedRows = [...stateRows];
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      [month]: newValue,
    };

    setStateRows(updatedRows);
  };

  const handleForecastBlur = (month: string, value: any, rowIndex: number) => {
  };

  async function handleAddMainChartAccountRow(type: string) {
    const hasValidationError = !accountNumber.trim() || !description.trim();

    if (hasValidationError) {
      setStateRows((prevRows) => {
        const newRows = [...prevRows];
        const lastIndex = newRows.length - 1;
        if (lastIndex >= 0) {
          newRows[lastIndex].validationError = true;
        }
        return newRows;
      });
      return;
    }

    const masterChartAccountsVO = {
      id: 0,
      clientId: userDetails.clientId,
      typeId: typeId,
      financialYear: forecastYear,
      accountNumber: accountNumber,
      accountName: description,
      accountType: type,
      isActive: constants.IS_ACTIVE_YES,
    };

    await toAddMasterChartAccounts(masterChartAccountsVO);

    const newRow = {
      accountNumber,
      accountName: description,
      validationError: false,
    };

    setStateRows((prevRows) => [
      ...prevRows.map((r) => ({ ...r, validationError: false })),
      newRow,
    ]);

    setAccountNumber("");
    setDescription("");
  }

  const handleStateRowsChange = (updatedRows: any[]) => {
    setUpdatedSubchartRows(updatedRows);
  };

  return (
    <div>
      <TableContainer sx={{ overflow: "auto", }}>
        <StyledTable
          stickyHeader
          aria-label="sticky table"
          size="small"
          sx={{
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "none",
              borderTop: "none",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align} className="cellTask"  >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ?? loadingFlag ? (
              <TableRow >
                <TableCell colSpan={columns.length} align="center">
                  <div >
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <span style={{ fontSize: "20px" }}>Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : stateRows?.length === 0 && pageType !== "BudgetAccountDetails" ? (
              <TableRow>
                <TableCell align="center" colSpan={columns.length}>
                  {translate('noRecordsFound')}
                </TableCell>
              </TableRow>
            ) : (
              stateRows?.map((row, index) => {
                const isRowExpanded = expandedRows.includes(index);
                return (
                  <React.Fragment key={index}>
                    <TableRow
                      sx={{
                        backgroundColor: isExpand && pageType !== "BudgetImport" && pageType !== "budgetImported" ? "#f1f3f4" : index % 2 === 0 ? "#f1f3f4" : "#fff",
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell key={column.id} align={column.align} className="left-aligned-text" style={{ minWidth: column?.minWidth ? column.minWidth : "auto", font: '400 16px/1.2 "Inter", sans-serif' }} >
                          {/* {column.id === "accountNumber" &&
                            <IconButton size="small" onClick={() => handleExpandClick(index, row)}>
                              {isRowExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>} */}

                          {column.id === "dioceseGLMapping" ?

                            row.dioceseGLMapping ? row.dioceseGLMapping.displayName : "-"
                            : column.id === "editDelete" && pageType === "BudgetAccountDetails" ? (
                              <Box display="flex" gap={1} justifyContent="center" >
                                {editingRowIndex === index && editFlags[index] ? (
                                  <DoneIcon
                                    sx={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSaveClick(index, row as MasterChartAccountsVO);
                                    }}
                                  />
                                ) : (
                                  <EditIcon
                                    style={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditClick(index, row);
                                    }}
                                  />
                                )}
                                <DeleteIcon
                                  sx={{ color: "error.main", cursor: "pointer" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(index, row);
                                  }}
                                />
                              </Box>
                            ) : column.id === "accountNumber" ? (
                              editingRowIndex === index && editFlags[index] ? (
                                <Grid container direction="row" alignItems="center">
                                  <Grid item lg={1}>
                                    <IconButton size="small" onClick={() => handleExpandClick(index, row)}>
                                      {isRowExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    </IconButton>
                                  </Grid>
                                  <Grid item lg={10}>
                                    <CustomTextField
                                      fullWidth
                                      value={row[column.id]}
                                      onChange={(e) => {
                                        const numericValue = e.target.value;
                                        if (/^\d*$/.test(numericValue)) {
                                          const updatedRows = [...stateRows];
                                          updatedRows[index][column.id] = numericValue;
                                          setStateRows(updatedRows);
                                        }
                                      }}
                                      inputProps={{ maxLength: 16, inputMode: "numeric", pattern: "[0-9]*" }}
                                      style={{ marginLeft: 4 }}
                                    />
                                  </Grid>
                                </Grid>
                              ) : (
                                // Non-editable: Icon centered
                                <Box display="flex" alignItems="center" justifyContent="center">
                                  <IconButton size="small" onClick={() => handleExpandClick(index, row)}>
                                    {isRowExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                  <Box ml={1}>
                                    {row[column.id] && Number(row[column.id]) < 10000 ? row[column.id] : " "}
                                  </Box>
                                </Box>
                              )
                            ) : editingRowIndex === index && editFlags[index] && column.id === "accountName" ? (
                              <Grid container direction="row"  >
                                <Grid item lg={10}>
                                  <CustomTextField
                                    value={row[column.id]}
                                    onChange={(e) => {
                                      const updatedRows = [...stateRows];
                                      updatedRows[index][column.id] = e.target.value;
                                      setStateRows(updatedRows);
                                    }}
                                    inputProps={{ maxLength: 100 }}
                                  />
                                </Grid>
                              </Grid>
                            ) : (CommonUtilities.monthRegularList.includes(column.id) ?
                              <Box position="relative" display="inline-block">
                                <Box position="absolute" top={10} bgcolor="white" zIndex={1}>
                                  {" $ "}
                                </Box>
                                <CustomTextField
                                  value={row[column.id] ?? ""}
                                  onChange={(e) => handleForecastChange(column.id, e.target.value, index)}
                                  onBlur={() => handleForecastBlur(column.id, row[column.id], index)}
                                  inputProps={{
                                    maxLength: 13,
                                    style: { textAlign: "right", paddingLeft: 0, marginRight: 0.1 },
                                  }}
                                  sx={{ marginLeft: 0.5, pl: 0 }}
                                />
                              </Box> :
                              (column.id === "accountNumber" ? (row[column.id] && Number(row[column.id]) < 10000 ? row[column.id] : " ") : (row[column.id] ? row[column.id] : row[column.id]))
                            )
                          }

                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={columns.length} sx={{ padding: 0, border: "none" }}>
                        <Collapse in={isRowExpanded} timeout="auto" unmountOnExit>
                          {(() => {
                            const perRowSubs = subChartAccountsList[index] ?? (pageType === "BudgetAccountDetails" ? (row.masterSubChartAccountsList ?? []) : (row.forecastSubChartAccountsList ?? []));
                            const transformed = transformSubAccounts(perRowSubs);
                            return (
                              <ReusableSubChartsAccountTable
                                tableKey="subChartAccountsListing"
                                columns={filterColumns(`${tableKey}`)}
                                rows={transformed}
                                chartAccountsVOList={chartAccountsVOList}
                                loading={loading}
                                readOnlyForecast={readOnlyForecast}
                                mainRow={row}
                                forecastYear={forecastYear}
                                isExpand={isExpand}
                                pageType={pageType}
                                type={pageType === "ActualsDetails" ? row.accountType : transformed[0]?.accountType ?? ""}
                                onStateRowsChange={handleStateRowsChange}
                                setToRefresh={setToRefresh}
                              />
                            );
                          })()}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
            {!loading && pageType !== "BudgetImport" && pageType !== "budgetImported" && pageType !== "actualsImported" && (pageType !== "BudgetAccountDetails" ? (
              <TableRow>
                <TableCell></TableCell>
                {(tableKey === "chartBudgetExpensesAccountsListing" || tableKey === "chartBudgetIncomeAccountsListing") && <TableCell></TableCell>}
                <TableCell align="right" style={{ fontSize: 18, paddingRight: 10 }}>{translate('total')}</TableCell>
                <TableCell align="right" style={{ fontSize: 18, paddingRight: 10 }}>
                  {totalAmount ? formatCurrency(totalAmount) : "$0.00"}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell align="center">
                  <Grid container direction="row"  >
                    <Grid item lg={1}>
                    </Grid>
                    <Grid item lg={10.2}>
                      <CustomTextField
                        fullWidth
                        value={accountNumber}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (/^\d{0,16}$/.test(newValue)) {
                            setAccountNumber(newValue);
                          }
                        }}
                        inputProps={{ maxLength: 16, inputMode: "numeric", pattern: "[0-9]*" }}
                        error={!accountNumber && stateRows[stateRows.length - 1]?.validationError}
                        helperText={!accountNumber && stateRows[stateRows.length - 1]?.validationError ? translate('chartAccountIsRequired') : ""}
                      />
                    </Grid>
                  </Grid>
                </TableCell>
                <TableCell align="left">
                  <Grid container direction="row"  >

                    <Grid item lg={10.2}>
                      <CustomTextField
                        fullWidth
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        inputProps={{ maxLength: 100 }}
                        error={!description && stateRows[stateRows.length - 1]?.validationError}
                        helperText={!description && stateRows[stateRows.length - 1]?.validationError ? translate('descriptionIsRequired') : ""}
                      />
                    </Grid>
                  </Grid>
                </TableCell>
                <TableCell align="center" style={{ fontSize: 16, paddingRight: 10 }} onClick={() => handleAddMainChartAccountRow(type ?? "")}>
                  <ControlPointIcon style={{ marginBottom: -5, cursor: "pointer" }} />
                  {translate('mainChartAccount')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </div>
  );

};

export default ReusableChartsAccountTable;
